import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/LoadingButton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  usePatchAgentsById,
  getGetAgentsQueryKey,
} from '@/api/control/control-plane/control-plane'
import type { AgentDto } from '@/api/control/missionImpactAPI.schemas'
import { useError } from '@/contexts/ErrorContext'
import { useLanguage } from '@/contexts/LanguageContext'

/** Lowest number of tasks an agent may run in parallel. */
const MIN_WORKER_COUNT = 1
/** Highest number of tasks an agent may run in parallel. */
const MAX_WORKER_COUNT = 10

interface EditAgentDialogProps {
  orgId: string
  agent: AgentDto
}

/**
 * Dialog that allows editing an agent's name and how many tasks it runs in parallel.
 */
export function EditAgentDialog({ orgId, agent }: EditAgentDialogProps) {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const { handleApiError } = useError()
  const [isOpen, setIsOpen] = useState(false)
  const [name, setName] = useState(agent.name)
  const [workerCount, setWorkerCount] = useState(String(agent.workerCount))

  const { mutate: patchAgent, isPending } = usePatchAgentsById({
    mutation: {
      onSuccess: () => {
        toast.success(t.agents.editDialog.success)
        void queryClient.invalidateQueries({
          queryKey: getGetAgentsQueryKey(orgId),
        })
        setIsOpen(false)
      },
      onError: (error) => {
        handleApiError(error, { context: 'EditAgentDialog.patchAgent' })
      },
    },
  })

  const handleSubmit = () => {
    patchAgent({ orgId, agentId: agent.id, data: { name, workerCount: parsedWorkerCount } })
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setName(agent.name)
      setWorkerCount(String(agent.workerCount))
    }
  }

  const parsedWorkerCount = Number(workerCount)
  const isWorkerCountValid =
    Number.isInteger(parsedWorkerCount) &&
    parsedWorkerCount >= MIN_WORKER_COUNT &&
    parsedWorkerCount <= MAX_WORKER_COUNT
  const hasChanges = name.trim() !== agent.name || parsedWorkerCount !== agent.workerCount

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t.agents.editDialog.triggerLabel}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t.agents.editDialog.title}</DialogTitle>
          <DialogDescription>
            {t.agents.editDialog.description}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-agent-name">{t.agents.editDialog.nameLabel}</Label>
            <Input
              id="edit-agent-name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              placeholder={t.agents.editDialog.namePlaceholder}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-agent-worker-count">{t.agents.editDialog.workerCountLabel}</Label>
            <Input
              id="edit-agent-worker-count"
              type="number"
              min={MIN_WORKER_COUNT}
              max={MAX_WORKER_COUNT}
              step={1}
              value={workerCount}
              onChange={(e) => { setWorkerCount(e.target.value); }}
            />
            <p className="text-xs text-muted-foreground">
              {isWorkerCountValid
                ? t.agents.editDialog.workerCountDescription
                : t.agents.editDialog.workerCountRange}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { setIsOpen(false); }}
            disabled={isPending}
          >
            {t.agents.editDialog.cancel}
          </Button>
          <LoadingButton
            type="button"
            onClick={handleSubmit}
            disabled={!hasChanges || !name.trim() || !isWorkerCountValid}
            isLoading={isPending}
          >
            {t.agents.editDialog.save}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
